import React, { useEffect, useRef, useState } from 'react';
import { useSelfScheduleFlags } from '../../../../hooks/useSelfScheduleVariant';
import { ON_PAGE_FORM_CONTAINER_IDS } from '../../../../utils/selfScheduleConfig';
import './StickyCTA.css';

interface MobileStickyCTAProps {
    heroIsFirst?: boolean;
}

export function MobileStickyCTA({ heroIsFirst = true }: MobileStickyCTAProps) {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const expandTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // WEBDEV-4239: which form container is on the page is decided client-side,
    // when Optimizely resolves the variant — which can land after this component
    // has mounted. Either flag can swap the form out (see useSelfScheduleFlags),
    // so re-resolve the observed containers whenever either changes, otherwise
    // the effect holds a detached node belonging to a replaced form.
    const { active, onPageFormActive } = useSelfScheduleFlags();

    useEffect(() => {
        const hero = document.getElementById('hero-cta') || document.querySelector('.hero-cta');
        if (!hero || typeof IntersectionObserver === 'undefined') return;

        const applyState = (show: boolean) => {
            setVisible(show);
            if (show) {
                if (expandTimer.current) clearTimeout(expandTimer.current);
                expandTimer.current = setTimeout(() => setExpanded(true), 150);
            } else {
                if (expandTimer.current) {
                    clearTimeout(expandTimer.current);
                    expandTimer.current = null;
                }
                setExpanded(false);
            }
        };

        // When heroIsFirst is false, also observe the first section (PromoModule)
        // so the sticky hides whenever either it or the hero is in view.
        const firstSection = !heroIsFirst ? document.getElementById('promo-module') : null;

        // Initialize conservatively so the sticky stays hidden until the observer
        // reports the real intersection state. firstSectionVisible is only relevant
        // when firstSection exists — default false so it doesn't block the control.
        let heroVisible = true;
        let firstSectionVisible = firstSection !== null;
        // Form containers currently observed, and the subset of them in view.
        // Both start empty: the observer's first callback reports the real
        // intersection state for every target it is given, so measuring up
        // front would only be overwritten (and costs a reflow per container).
        const forms = new Set<Element>();
        const visibleForms = new Set<Element>();

        const recompute = () => applyState(!heroVisible && !firstSectionVisible && visibleForms.size === 0);

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target === hero) heroVisible = entry.isIntersecting;
                    else if (firstSection && entry.target === firstSection) firstSectionVisible = entry.isIntersecting;
                    else if (forms.has(entry.target)) {
                        if (entry.isIntersecting) visibleForms.add(entry.target);
                        else visibleForms.delete(entry.target);
                    }
                });
                recompute();
            },
            { threshold: 0 }
        );

        io.observe(hero);
        if (firstSection) io.observe(firstSection);

        // Observe whichever on-page form container is in the DOM — the legacy
        // ConsultationForm or the self-schedule one. Only one is mounted at a
        // time, but resolving every id keeps this agnostic to the variant swap
        // (and to a page that someday renders both). Drops containers that have
        // been detached so a replaced form can't keep the pill suppressed.
        const syncForms = () => {
            let changed = false;
            forms.forEach((el) => {
                if (el.isConnected) return;
                io.unobserve(el);
                forms.delete(el);
                visibleForms.delete(el);
                changed = true;
            });
            ON_PAGE_FORM_CONTAINER_IDS.forEach((id) => {
                const el = document.getElementById(id);
                if (!el || forms.has(el)) return;
                forms.add(el);
                io.observe(el);
                changed = true;
            });
            // A detached container is dropped from visibleForms without the
            // observer reporting it, so recompute here too. Guarded on `changed`
            // because applyState restarts the expand timer — running it on every
            // unrelated mutation would keep the pill from ever expanding.
            if (changed) recompute();
        };
        syncForms();

        // The form is in the DOM by the time this first runs only because every
        // page happens to mount the sticky after it, and nothing enforces that
        // — reordering the JSX, moving the sticky into a portal or deferring the
        // form's own (heavy Formik) mount would leave nothing to observe and put
        // the pill back on top of the form, silently and only under the variant.
        // Re-syncing on DOM changes makes the resolution self-healing instead of
        // render-order dependent; the callback is two getElementById lookups per
        // batch of mutation records.
        const mo = new MutationObserver(syncForms);
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            io.disconnect();
            mo.disconnect();
            if (expandTimer.current) clearTimeout(expandTimer.current);
        };
    }, [heroIsFirst, active, onPageFormActive]);

    return (
        <a
            href="#googlelp-quote"
            aria-hidden={!visible}
            tabIndex={visible ? 0 : -1}
            className={`rounded-pill border-none mcl-font-proxima sticky-cta--bottom button-cta ${
                visible ? 'sticky-cta--visible' : ''
            } ${expanded ? 'sticky-cta--expanded px-4' : ''}`}
        >
            <span className="sticky-cta-inner">
                <span className="sticky-cta-text">Get started</span>
                <span className="sticky-cta-arrow" aria-hidden>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M5 12h14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M12 5l7 7-7 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </span>
        </a>
    );
}

export default MobileStickyCTA;
